import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const EMPTY = {
  subject: "", dueDate: "",
  contactId: "", accountId: "", dealId: "",
  status: "NOT_STARTED", priority: "NORMAL",
  reminderAt: "", repeatRule: "", description: "",
};
type TaskForm = typeof EMPTY;

export default function TaskNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [contacts, setContacts] = useState<{ id: string; name: string }[]>([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [deals, setDeals]       = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<TaskForm>(EMPTY);

  useEffect(() => {
    api.get("/contacts", { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } }).then((r) => setContacts(r.data.data ?? []));
    api.get("/accounts", { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } }).then((r) => setAccounts(r.data.data ?? []));
    api.get("/deals",    { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } }).then((r) => setDeals(r.data.data ?? []));
  }, []);

  function set<K extends keyof TaskForm>(k: K, v: TaskForm[K]) { setForm((f) => ({ ...f, [k]: v })); }

  async function save(mode: "save" | "saveAndNew") {
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        payload[k] = v;
      }
      await api.post("/tasks", payload);
      qc.invalidateQueries({ queryKey: ["/tasks"] });
      if (mode === "saveAndNew") setForm(EMPTY);
      else navigate("/tasks");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create task.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="Create Task" description="Standalone or linked to a contact, account, or deal." />
      <form onSubmit={(e) => { e.preventDefault(); save("save"); }} className="p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <Section title="Task Information">
            <Field id="subject" label="Subject" required className="md:col-span-2">
              <Input id="subject" required value={form.subject} onChange={(e) => set("subject", e.target.value)} />
            </Field>
            <Field id="dueDate" label="Due Date">
              <Input id="dueDate" type="date" value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} />
            </Field>
            <Field id="reminderAt" label="Reminder">
              <Input id="reminderAt" type="datetime-local" value={form.reminderAt} onChange={(e) => set("reminderAt", e.target.value)} />
            </Field>
            <Field id="status" label="Status">
              <Select id="status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="NOT_STARTED">Not started</option>
                <option value="IN_PROGRESS">In progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="DEFERRED">Deferred</option>
                <option value="WAITING">Waiting on someone</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            </Field>
            <Field id="priority" label="Priority">
              <Select id="priority" value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                <option value="LOW">Low</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
              </Select>
            </Field>
            <Field id="contactId" label="Contact">
              <Select id="contactId" value={form.contactId} onChange={(e) => set("contactId", e.target.value)}>
                <option value="">—</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field id="accountId" label="Account">
              <Select id="accountId" value={form.accountId} onChange={(e) => set("accountId", e.target.value)}>
                <option value="">—</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </Field>
            <Field id="dealId" label="Deal">
              <Select id="dealId" value={form.dealId} onChange={(e) => set("dealId", e.target.value)}>
                <option value="">—</option>
                {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>
            <Field id="repeatRule" label="Repeat">
              <Select id="repeatRule" value={form.repeatRule} onChange={(e) => set("repeatRule", e.target.value)}>
                <option value="">Does not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </Select>
            </Field>
          </Section>

          <Section title="Description">
            <div className="md:col-span-2">
              <textarea
                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Details…"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={!!form.subject}
            onCancel={() => navigate("/tasks")}
            onSaveAndNew={() => save("saveAndNew")}
            onSubmit={() => save("save")}
            error={error}
          />
        </div>
      </form>
    </>
  );
}
